import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  inject,
} from '@angular/core';
import { Datastore } from '@freelancer/datastore';
import type { SiteStatsCollection } from '@freelancer/datastore/collections';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'app-marketplace-stats',
  template: `
    <div
      class="MarketplaceStats"
      [class.MarketplaceStats--dark]="darkMode"
    >
      <div class="MarketplaceStats-wrapper">
        <h2
          class="text-xlarge MarketplaceStats-header mb-mid"
          i18n="Marketplace Statistics header"
          [class.text-light]="darkMode"
          [class.text-foreground]="!darkMode"
        >
          The World's Largest Marketplace
        </h2>

        <p
          class="mb-xxlarge text-large MarketplaceStats-description text-center {{
            darkMode ? 'text-light' : 'text-foreground'
          }}"
          i18n="Marketplace statistics description"
        >
          Millions of users, from small businesses to large enterprises,
          entrepreneurs to startups, use Freelancer to turn their ideas into
          reality.
        </p>
        <div class="MarketplaceStats-info-container">
          <div
            class="MarketplaceStats-info MarketplaceStats-user mb-small desktop:mb-none"
          >
            <p
              class="text-color-inherit text-size-inherit MarketplaceStats-info-number text-center"
              i18n="Number of users (in millions) on the site"
            >
              {{ userCount$ | flAsync }}M
            </p>
            <p
              class="text-large MarketplaceStats-info-label text-center {{
                darkMode ? 'text-light' : 'text-foreground'
              }}"
              i18n="Number of users on the site header"
            >
              Registered Users
            </p>
          </div>
          <div class="MarketplaceStats-info">
            <p
              class="text-color-inherit text-size-inherit MarketplaceStats-info-number text-center"
              i18n="Number of projects (in millions) posted on the site"
            >
              {{ projectCount$ | flAsync }}M
            </p>
            <p
              class="text-large MarketplaceStats-info-label text-center {{
                darkMode ? 'text-light' : 'text-foreground'
              }}"
              i18n="Number of jobs posted on the site header"
            >
              Total Jobs Posted
            </p>
          </div>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./marketplace-stats.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarketplaceStatsComponent implements OnInit {
  private datastore = inject(Datastore);

  userCount$: Observable<string>;
  projectCount$: Observable<string>;

  @Input() darkMode = false;

  ngOnInit(): void {
    const siteStats$ = this.datastore
      .document<SiteStatsCollection>('siteStats')
      .valueChanges();

    this.userCount$ = siteStats$.pipe(
      map(stat => (stat.userCount / 1_000_000).toFixed(1)),
    );

    this.projectCount$ = siteStats$.pipe(
      map(stat => (stat.projectCount / 1_000_000).toFixed(1)),
    );
  }
}
